import { isPlainObject } from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigChange, humanize, isSecretPath, isWholeRule } from './changes.ts';
import { getPassages } from '../fineTuning/applyRule.ts';
import ChangedPassages from '../fineTuning/ChangedPassages.tsx';
import { ListChips } from '../fineTuning/DiffChips.tsx';

const LONG_TEXT_LENGTH = 120;

const isEmpty = (value: unknown) => value === undefined || value === null || value === '';

const isLongText = (value: unknown) =>
    typeof value === 'string' && (value.includes('\n') || value.length > LONG_TEXT_LENGTH);

const isPrimitiveList = (value: unknown) =>
    isEmpty(value) || (Array.isArray(value) && value.every((item) => !isPlainObject(item) && !Array.isArray(item)));

const useFormatValue = () => {
    const { t } = useTranslation('config');
    return (value: unknown, path: string): React.ReactNode => {
        if (isEmpty(value)) return null;
        if (typeof value === 'boolean' && isSecretPath(path)) {
            return t(value ? 'configPage.history.change.secretSet' : 'configPage.history.change.secretNotSet');
        }
        if (typeof value === 'boolean') {
            return t(value ? 'configPage.history.change.on' : 'configPage.history.change.off');
        }
        if (typeof value === 'string' || typeof value === 'number') return String(value);
        return JSON.stringify(value);
    };
};

interface StruckArrowProps {
    before: React.ReactNode;
    after: React.ReactNode;
}

const StruckArrow = ({ before, after }: StruckArrowProps) => {
    const { t } = useTranslation('config');
    const empty = <span className="text-content-weak">{t('configPage.history.change.empty')}</span>;
    return (
        <span className="break-words text-sm">
            {isEmpty(before) ? empty : <span className="text-content-medium line-through">{before}</span>}
            <span className="text-content-medium"> → </span>
            {isEmpty(after) ? empty : <span className="font-medium text-content-strong">{after}</span>}
        </span>
    );
};

interface ObjectLinesProps {
    path: string;
    before: object;
    after: object;
}

const ObjectLines = ({ path, before, after }: ObjectLinesProps) => {
    const format = useFormatValue();
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
        (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key])
    );
    return (
        <span className="flex flex-col gap-1">
            {keys.map((key) => (
                <span key={key} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-xs text-content-medium">{humanize(key)}</span>
                    <StruckArrow
                        before={format(before[key], `${path}.${key}`)}
                        after={format(after[key], `${path}.${key}`)}
                    />
                </span>
            ))}
        </span>
    );
};

interface ChangeValueProps {
    change: ConfigChange;
}

const ChangeValue = ({ change }: ChangeValueProps) => {
    const { t } = useTranslation('config');
    const format = useFormatValue();
    const { path, before, after } = change;

    if (isWholeRule(change)) {
        return (
            <span className="text-sm font-medium text-content-strong">
                {t(isEmpty(before) ? 'configPage.history.change.ruleAdded' : 'configPage.history.change.ruleRemoved')}
            </span>
        );
    }
    if (isLongText(before) || isLongText(after)) {
        return <ChangedPassages passages={getPassages(String(before ?? ''), String(after ?? ''))} />;
    }
    if (isPrimitiveList(before) && isPrimitiveList(after) && (Array.isArray(before) || Array.isArray(after))) {
        return (
            <ListChips
                before={((before as unknown[]) ?? []).map(String)}
                after={((after as unknown[]) ?? []).map(String)}
            />
        );
    }
    if (isPlainObject(before) || isPlainObject(after)) {
        return <ObjectLines path={path} before={(before as object) ?? {}} after={(after as object) ?? {}} />;
    }
    return <StruckArrow before={format(before, path)} after={format(after, path)} />;
};

export default React.memo(ChangeValue);
